/**
 * WizardStepTemplates.jsx
 * Componentes de step reutilizáveis para wizards
 */

import React, { Suspense, lazy } from 'react';
import {
 Box, Grid, Typography, CircularProgress 
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';
import PremiumInput from './PremiumInput';

// Lazy load EditAttributesAccordion
const EditAttributesAccordion = lazy(() =>
  import('../../../settings/components/EditAttributesAccordion')
);

// ============================================================================
// HELPER: Colors
// ============================================================================

export const getWizardColors = (tokens) => ({
  accent: tokens?.accent?.primary || '#00d4aa',
  accentSubtle: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
  accentHover: tokens?.accent?.hover || 'rgba(0, 212, 170, 0.15)',
  textPrimary: tokens?.text?.primary || '#f0f6fc',
  textSecondary: tokens?.text?.secondary || '#8b949e',
  textMuted: tokens?.text?.muted || '#6e7681',
  surfaceBase: tokens?.surface?.base || '#0d1117',
  surfaceElevated: tokens?.surface?.elevated || '#161b22',
  surfaceHover: tokens?.surface?.hover || 'rgba(255, 255, 255, 0.05)',
  borderSubtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
  borderDefault: tokens?.border?.default || 'rgba(255, 255, 255, 0.1)',
  success: tokens?.semantic?.success || '#00d4aa',
  error: tokens?.semantic?.error || '#f85149',
});

// ============================================================================
// SECTION TITLE
// ============================================================================

export const SectionTitle = ({ children, colors }) => {
  const tokens = usePremiumTokens();
  const c = colors || getWizardColors(tokens);

  return (
    <Typography
      variant="overline"
      sx={{
        color: c.textMuted,
        fontWeight: 600,
        letterSpacing: '0.1em',
        fontSize: '11px',
        pb: 1,
        borderBottom: `1px solid ${c.borderSubtle}`,
      }}
    >
      {children}
    </Typography>
  );
};

// ============================================================================
// BASIC INFO STEP TEMPLATE
// ============================================================================

/**
 * BasicInfoStepTemplate
 * Template para step de informações básicas (nome, identificador, grupo)
 *
 * @param {Object} props
 * @param {Object} props.data - Dados do formulário
 * @param {Function} props.updateData - Função para atualizar dados
 * @param {Object} props.errors - Erros de validação
 * @param {Array} props.fields - Campos a exibir [{ key, label, required, type, placeholder, ... }]
 * @param {Array} props.sections - Seções com campos [{ title, fields: [...] }]
 */
export const BasicInfoStepTemplate = ({
  data,
  updateData,
  errors,
  fields = [],
  sections = [],
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const renderField = (field) => {
    const value = field.key.includes('.')
      ? field.key.split('.').reduce((acc, part) => acc?.[part], data)
      : data[field.key];

    const handleChange = (newValue) => {
      if (field.key.includes('.')) {
        const keys = field.key.split('.');
        const newData = { ...data };
        let current = newData;

        for (let i = 0; i < keys.length - 1; i++) {
          current[keys[i]] = { ...current[keys[i]] };
          current = current[keys[i]];
        }

        current[keys[keys.length - 1]] = newValue;
        updateData(newData);
      } else {
        updateData({ [field.key]: newValue });
      }
    };

    if (field.component) {
      const CustomComponent = field.component;
      return (
        <CustomComponent
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          error={!!errors?.[field.key]}
          helperText={errors?.[field.key]}
          {...field.props}
        />
      );
    }

    return (
      <PremiumInput
        label={`${field.label}${field.required ? ' *' : ''}`}
        value={value || ''}
        onChange={(e) => handleChange(e.target.value)}
        error={!!errors?.[field.key]}
        helperText={errors?.[field.key]}
        placeholder={field.placeholder}
        type={field.type || 'text'}
        multiline={field.multiline}
        rows={field.rows}
        autoFocus={field.autoFocus}
        disabled={field.disabled}
        inputProps={field.inputProps}
      />
    );
  };

  // Se tiver seções
  if (sections.length > 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {sections.map((section, sectionIndex) => (
          <Box key={sectionIndex} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {section.title && <SectionTitle colors={colors}>{section.title}</SectionTitle>}
            <Grid container spacing={3}>
              {section.fields.map((field) => (
                <Grid
                  key={field.key}
                  size={{ xs: 12, sm: field.fullWidth ? 12 : field.sm || 6, md: field.md }}
                  sx={{ minWidth: field.minWidth || 180 }}
                >
                  {renderField(field)}
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>
    );
  }

  // Se tiver apenas fields
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        {fields.map((field) => (
          <Grid
            key={field.key}
            size={{ xs: 12, sm: field.fullWidth ? 12 : field.sm || 6, md: field.md }}
            sx={{ minWidth: field.minWidth || 180 }}
          >
            {renderField(field)}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ============================================================================
// REVIEW STEP TEMPLATE
// ============================================================================

/**
 * ReviewStepTemplate
 * Template genérico para step de revisão final
 *
 * @param {Object} props
 * @param {Object} props.data - Dados do formulário
 * @param {Array} props.sections - Seções a exibir [{ title, icon, items: [{ label, value }] }]
 */
export const ReviewStepTemplate = ({ data, sections = [] }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const Section = ({ title, icon, children }) => (
    <Box
      sx={{
        backgroundColor: colors.surfaceElevated,
        borderRadius: '12px',
        border: `1px solid ${colors.borderSubtle}`,
        p: 2.5,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon && React.cloneElement(icon, { sx: { fontSize: 18, color: colors.accent } })}
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
          }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );

  const InfoRow = ({ label, value, highlight }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography
          variant="caption"
          sx={{ color: colors.textMuted, fontSize: '11px' }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: highlight ? colors.accent : colors.textPrimary,
            fontWeight: highlight ? 600 : 400,
            textAlign: 'right',
            maxWidth: '60%',
          }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header de confirmação */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <CheckCircleIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm') || 'Revise os dados antes de salvar'}
        </Typography>
      </Box>

      {/* Seções */}
      <Grid container spacing={2}>
        {sections.map((section, index) => (
          <Grid key={index} size={{ xs: 12, md: 6 }}>
            <Section title={section.title} icon={section.icon}>
              {section.items?.map((item, itemIndex) => (
                <InfoRow
                  key={itemIndex}
                  label={item.label}
                  value={item.value}
                  highlight={item.highlight}
                />
              ))}
              {section.render && section.render(data, colors)}
            </Section>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ============================================================================
// ATTRIBUTES STEP TEMPLATE
// ============================================================================

/**
 * AttributesStepTemplate
 * Template para step de atributos customizados
 */
export const AttributesStepTemplate = ({
  data,
  updateData,
  attributeDefinitions = {},
  filterKeys = [],
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  // Filtrar atributos (remover keys especiais)
  const filterAttributes = (attributes) => {
    if (!attributes) return {};
    const filtered = {};
    Object.keys(attributes).forEach((key) => {
      if (!filterKeys.includes(key)) {
        filtered[key] = attributes[key];
      }
    });
    return filtered;
  };

  // Atualizar atributos preservando keys filtradas
  const handleAttributesChange = (newAttributes) => {
    const preserved = {};
    filterKeys.forEach((key) => {
      if (data.attributes?.[key] !== undefined) {
        preserved[key] = data.attributes[key];
      }
    });
    updateData({
      attributes: {
        ...newAttributes,
        ...preserved,
      },
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Info banner */}
      <Box
        sx={{
          p: 2,
          borderRadius: '10px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('wizardAttributesHelp') || 'Configure atributos personalizados conforme necessário.'}
        </Typography>
      </Box>

      <Suspense
        fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} sx={{ color: colors.accent }} />
          </Box>
        }
      >
        <EditAttributesAccordion
          attributes={filterAttributes(data.attributes)}
          setAttributes={handleAttributesChange}
          definitions={attributeDefinitions}
        />
      </Suspense>
    </Box>
  );
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  BasicInfoStepTemplate,
  ReviewStepTemplate,
  AttributesStepTemplate,
  SectionTitle,
  getWizardColors,
};
